import {engineContext} from './context.js';

/* ZooInvest Evidence Ledger / Point-in-Time Engine v0.1.0-alpha
 * Engine-only. No UI changes.
 * Purpose: provenance, freshness, anti-lookahead, immutable snapshots.
 * Thresholds are provisional. Missing stays missing.
 */
(function(root,factory){
  const api=factory();
  if(typeof module==='object'&&module.exports) module.exports=api;
  root.ZooInvestEvidenceLedger=api;
})(engineContext,function(){
  'use strict';
  const VERSION='0.1.0-alpha';
  const CALIBRATED=false;
  const STATUS=Object.freeze({VERIFIED:'VERIFIED',PARTIAL:'PARTIAL',UNVERIFIED:'UNVERIFIED',OPEN:'OPEN'});
  const FRESHNESS=Object.freeze({FRESH:'FRESH',AGING:'AGING',STALE:'STALE',UNKNOWN:'UNKNOWN'});
  const DEFAULT_TTL_DAYS=Object.freeze({
    price:2,market:2,news:3,consensus:14,ownership:45,fundamental_quarterly:150,
    fundamental_annual:430,relationship:365,geography:430,filing:430,other:90
  });
  function deepFreeze(o){if(!o||typeof o!=='object'||Object.isFrozen(o))return o;Object.freeze(o);Object.values(o).forEach(deepFreeze);return o;}
  function round(v,d=3){return typeof v==='number'&&Number.isFinite(v)?Number(v.toFixed(d)):null;}
  function parseDate(v){if(!v)return null;const d=new Date(v);return Number.isNaN(d.getTime())?null:d;}
  function iso(v){const d=parseDate(v);return d?d.toISOString():null;}
  function daysBetween(a,b){const x=parseDate(a),y=parseDate(b);return x&&y?Math.floor((y-x)/86400000):null;}
  function normStatus(s){s=String(s||'').toUpperCase();return STATUS[s]||STATUS.OPEN;}
  function categoryKey(v){return String(v||'other').trim().toLowerCase().replace(/[^a-z0-9]+/g,'_');}
  function ttlFor(category,override){if(Number.isFinite(override)&&override>0)return override;const k=categoryKey(category);return DEFAULT_TTL_DAYS[k]||DEFAULT_TTL_DAYS.other;}
  function makeId(prefix='ev'){return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,9)}`;}
  function result(engine,payload){return deepFreeze({engine,version:VERSION,calibrated:CALIBRATED,...payload});}

  function normalizeEvidence(input={}){
    const publishedAt=iso(input.publishedAt||input.filedAt||input.date||null);
    const observedAt=iso(input.observedAt||input.collectedAt||input.ingestedAt||publishedAt||null);
    const effectiveAt=iso(input.effectiveAt||input.periodEnd||input.asOf||publishedAt||null);
    const status=normStatus(input.status||input.verificationStatus);
    const source=input.source||input.sourceUrl||input.sourceId||null;
    const provider=input.provider||null;
    const metric=input.metric||input.field||null;
    const entityId=input.entityId||input.ticker||input.companyId||null;
    const critical=input.critical===true;
    const confidence=Number.isFinite(input.confidence)?Math.max(0,Math.min(1,input.confidence)):null;
    return result('EvidenceRecord',{
      id:input.id||makeId(),entityId,metric,category:categoryKey(input.category),value:input.value??null,
      unit:input.unit||null,status,confidence,critical,source,provider,publishedAt,observedAt,effectiveAt,
      sourcePresent:!!source,provenanceComplete:!!(source&&publishedAt),note:input.note||null
    });
  }

  function freshnessState(record={},opts={}){
    const r=record.engine==='EvidenceRecord'?record:normalizeEvidence(record);
    const asOf=iso(opts.asOf||new Date().toISOString());
    const ref=r.publishedAt||r.observedAt||r.effectiveAt;
    if(!ref||!asOf)return result('Freshness',{state:FRESHNESS.UNKNOWN,ageDays:null,ttlDays:ttlFor(r.category,opts.ttlDays)});
    const age=daysBetween(ref,asOf); const ttl=ttlFor(r.category,opts.ttlDays);
    if(age===null||age<0)return result('Freshness',{state:FRESHNESS.UNKNOWN,ageDays:age,ttlDays:ttl});
    const state=age<=ttl?FRESHNESS.FRESH:age<=Math.ceil(ttl*1.5)?FRESHNESS.AGING:FRESHNESS.STALE;
    return result('Freshness',{state,ageDays:age,ttlDays:ttl});
  }

  function usableAt(record,asOf){
    const r=record.engine==='EvidenceRecord'?record:normalizeEvidence(record);
    const t=parseDate(asOf); if(!t)return false;
    const pub=parseDate(r.publishedAt||r.observedAt); if(!pub)return false;
    return pub.getTime()<=t.getTime();
  }

  function pointInTimeSnapshot(input={}){
    const asOf=iso(input.asOf); if(!asOf)return result('PointInTimeSnapshot',{status:'OPEN',asOf:null,records:[],excludedFuture:[],missingReason:'Valid asOf is required.'});
    const src=Array.isArray(input.records)?input.records:[];
    const normalized=src.map(normalizeEvidence);
    const eligible=[],excludedFuture=[];
    for(const r of normalized){(usableAt(r,asOf)?eligible:excludedFuture).push(r);}
    const grouped=new Map();
    for(const r of eligible){
      const key=`${r.entityId||''}|${r.metric||''}|${r.category||''}`;
      const current=grouped.get(key);
      const rt=parseDate(r.publishedAt||r.observedAt)?.getTime()||0;
      const ct=current?(parseDate(current.publishedAt||current.observedAt)?.getTime()||0):-1;
      if(!current||rt>ct)grouped.set(key,r);
    }
    const records=[...grouped.values()].map(r=>deepFreeze({...r,freshness:freshnessState(r,{asOf}).state}));
    return result('PointInTimeSnapshot',{
      status:records.length?'READY':'OPEN',asOf,records,excludedFuture,sourceRecordCount:src.length,
      includedCount:records.length,excludedFutureCount:excludedFuture.length,
      antiLookahead:true,note:'Only evidence published/observed on or before asOf is eligible; later revisions cannot leak backwards.'
    });
  }

  function evidenceManifest(input={}){
    const asOf=iso(input.asOf||new Date().toISOString());
    const records=(Array.isArray(input.records)?input.records:[]).map(r=>r.engine==='EvidenceRecord'?r:normalizeEvidence(r));
    const counts={VERIFIED:0,PARTIAL:0,UNVERIFIED:0,OPEN:0};
    const freshness={FRESH:0,AGING:0,STALE:0,UNKNOWN:0};
    const criticalBlockers=[]; const sources=new Set();
    for(const r of records){
      counts[r.status]=(counts[r.status]||0)+1;
      const fs=freshnessState(r,{asOf}).state; freshness[fs]=(freshness[fs]||0)+1;
      if(r.source)sources.add(r.source);
      if(r.critical&&(r.status!==STATUS.VERIFIED||!r.sourcePresent||fs===FRESHNESS.STALE))criticalBlockers.push({id:r.id,entityId:r.entityId,metric:r.metric,status:r.status,freshness:fs,sourcePresent:r.sourcePresent});
    }
    const verifiedRatio=records.length?counts.VERIFIED/records.length:null;
    return result('EvidenceManifest',{
      asOf,total:records.length,counts,freshness,verifiedRatio:round(verifiedRatio),independentSources:sources.size,
      criticalBlockers,status:criticalBlockers.length?'BLOCKED':records.length?'READY':'OPEN',
      note:'Critical stale/unverified/missing-source facts remain visible blockers; no source is invented.'
    });
  }

  function appendLedger(history=[],entry={}){
    const prior=Array.isArray(history)?history:[];
    const normalized=normalizeEvidence(entry);
    const next=prior.map(x=>deepFreeze({...x})); next.push(normalized);
    return deepFreeze(next);
  }

  function detectRevision(records=[]){
    const rs=(Array.isArray(records)?records:[]).map(normalizeEvidence).filter(r=>r.entityId&&r.metric);
    const groups={};
    for(const r of rs){const k=`${r.entityId}|${r.metric}|${r.effectiveAt||''}`;(groups[k]||(groups[k]=[])).push(r);}
    const revisions=[];
    for(const [key,arr] of Object.entries(groups)){
      arr.sort((a,b)=>(parseDate(a.publishedAt||a.observedAt)?.getTime()||0)-(parseDate(b.publishedAt||b.observedAt)?.getTime()||0));
      for(let i=1;i<arr.length;i++) if(arr[i].value!==arr[i-1].value) revisions.push({key,from:arr[i-1].value,to:arr[i].value,oldRecordId:arr[i-1].id,newRecordId:arr[i].id,revisionPublishedAt:arr[i].publishedAt});
    }
    return result('RevisionDetector',{revisions,count:revisions.length,note:'Revisions are appended; historical point-in-time snapshots are not rewritten.'});
  }

  function selfTest(){
    const records=[
      {id:'a',entityId:'X',metric:'revenue',category:'fundamental_quarterly',value:100,status:'VERIFIED',source:'filing1',publishedAt:'2026-01-15',effectiveAt:'2025-12-31',critical:true},
      {id:'b',entityId:'X',metric:'revenue',category:'fundamental_quarterly',value:110,status:'VERIFIED',source:'filing2',publishedAt:'2026-04-15',effectiveAt:'2025-12-31',critical:true},
      {id:'c',entityId:'X',metric:'margin',category:'fundamental_quarterly',value:null,status:'OPEN',publishedAt:'2026-01-15',critical:false}
    ];
    const jan=pointInTimeSnapshot({asOf:'2026-02-01',records});
    const may=pointInTimeSnapshot({asOf:'2026-05-01',records});
    const stale=evidenceManifest({asOf:'2027-01-01',records:[records[0]]});
    const revisions=detectRevision(records);
    const h1=appendLedger([],records[0]); const h2=appendLedger(h1,{id:'z',entityId:'X',metric:'debt',status:'PARTIAL',source:'filing3',publishedAt:'2026-02-01'});
    const assertions=[
      ['future evidence is excluded from historical snapshot',jan.excludedFutureCount===1&&jan.records.find(x=>x.metric==='revenue').value===100],
      ['later snapshot can see later publication',may.records.find(x=>x.metric==='revenue').value===110],
      ['historical snapshot is immutable',Object.isFrozen(jan)&&Object.isFrozen(jan.records)],
      ['missing value stays missing',jan.records.find(x=>x.metric==='margin').value===null],
      ['stale critical evidence blocks manifest',stale.status==='BLOCKED'&&stale.criticalBlockers.length===1],
      ['revision detector appends revision instead of rewriting history',revisions.count===1&&revisions.revisions[0].from===100&&revisions.revisions[0].to===110],
      ['ledger is append-only',h1.length===1&&h2.length===2&&h1[0].id==='a'],
      ['source is never fabricated',normalizeEvidence({status:'VERIFIED'}).source===null],
      ['confirmed provenance completeness requires source and publication date',normalizeEvidence(records[0]).provenanceComplete===true],
      ['anti-lookahead flag is explicit',jan.antiLookahead===true]
    ];
    return deepFreeze({version:VERSION,passed:assertions.every(([,ok])=>!!ok),tests:assertions.map(([name,ok])=>({name,passed:!!ok}))});
  }

  const API={VERSION,CALIBRATED,STATUS,FRESHNESS,DEFAULT_TTL_DAYS,normalizeEvidence,freshnessState,usableAt,pointInTimeSnapshot,evidenceManifest,appendLedger,detectRevision,selfTest};
  try{API.selfTestResult=selfTest();}catch(e){API.bootstrapError=String(e&&e.message||e);}
  return Object.freeze(API);
});

